import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Clock4, Play, Square, Loader2, Target } from 'lucide-react';
import { WorkHoursChart, fmtMins } from '../components/WorkHoursChart';
import { TeamHoursSection } from '../components/TeamHoursSection';

const hhmmss = (secs) => {
  const s = Math.max(0, secs);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

function Stat({ label, value, sub, testId }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-heading text-foreground mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function MyTimePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.getMyTimeclock();
      setData(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = data?.open_entry;
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [open]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(false);
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!data?.enabled) {
    if ((data?.visible_count || 0) > 0) {
      return (
        <div className="space-y-6 animate-fade-in-up max-w-3xl" data-testid="my-time-page">
          <div>
            <h1 className="text-2xl font-heading text-foreground flex items-center gap-2"><Clock4 className="w-6 h-6 text-blue-400" /> My Time</h1>
            <p className="text-muted-foreground text-sm mt-1">Deine eigene Zeiterfassung ist nicht aktiviert — freigegebene Team-Stunden siehst du hier.</p>
          </div>
          <TeamHoursSection />
        </div>
      );
    }
    return (
      <div className="max-w-xl mx-auto mt-10 text-center text-muted-foreground" data-testid="my-time-disabled">
        <Clock4 className="w-8 h-8 mx-auto mb-3 opacity-50" />
        <p className="text-sm">Zeiterfassung ist für dich nicht aktiviert. Ein Admin kann dir im Bereich Team Analytics ein Wochen-Stundenziel setzen.</p>
      </div>
    );
  }

  const s = data.summary || {};
  const target = s.target_hours || 0;
  const elapsed = open ? Math.floor((Date.now() - new Date(open.clock_in).getTime()) / 1000) : 0;
  const weekPct = s.target_minutes ? Math.min(100, Math.round((s.week_minutes / s.target_minutes) * 100)) : 0;

  return (
    <div className="space-y-6 animate-fade-in-up max-w-3xl" data-testid="my-time-page">
      <div>
        <h1 className="text-2xl font-heading text-foreground flex items-center gap-2"><Clock4 className="w-6 h-6 text-blue-400" /> My Time</h1>
        <p className="text-muted-foreground text-sm mt-1">Deine erfasste Arbeitszeit — Wochenziel {target}h. Clock-in/out findest du auch unter dem Logo.</p>
      </div>

      <div className="bg-card border border-border rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="my-time-clock">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</div>
          <div className="text-lg font-heading text-foreground mt-0.5">
            {open ? <span className="text-emerald-400">Eingestempelt · <span className="font-mono tabular-nums" data-testid="my-time-elapsed">{hhmmss(elapsed)}</span></span> : <span className="text-muted-foreground">Ausgestempelt</span>}
          </div>
        </div>
        {open ? (
          <button onClick={() => act(api.clockOut, 'Ausgestempelt')} disabled={busy} data-testid="my-time-clock-out"
            className="inline-flex items-center gap-2 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors px-4 py-2.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />} Clock-out
          </button>
        ) : (
          <button onClick={() => act(api.clockIn, 'Eingestempelt')} disabled={busy} data-testid="my-time-clock-in"
            className="inline-flex items-center gap-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors px-4 py-2.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Clock-in
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat label="Diese Woche" value={fmtMins(s.week_minutes)} sub={`Ziel ${target}h · ${weekPct}%`} testId="my-time-week" />
        <Stat label="Dieser Monat" value={fmtMins(s.month_minutes)} testId="my-time-month" />
        <Stat label="Wochenziel" value={`${target}h`} sub="vom Admin gesetzt" testId="my-time-target" />
      </div>

      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-medium text-foreground">Stunden pro Woche · {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</h2>
        </div>
        <WorkHoursChart weeks={s.weeks || []} targetHours={target} />
      </div>

      <TeamHoursSection />
    </div>
  );
}
